import {trackEvent} from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/proteins/util/ProteinConstants';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import * as AlphaFoldDAO from './AlphaFoldDAO';
import {
  HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
} from './AlphaFoldTabContent';

export async function requestRun(
  proteinId: string,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery
): Promise<void> {
  await AlphaFoldDAO.runAlphaFold(proteinId);
  ApolloWrappers.writeQuery(
    {query: REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY},
    {
      ...remainingExecutionsAndTenantNameData,
      remainingAlphafoldExecutions: remainingExecutionsAndTenantNameData.remainingAlphafoldExecutions - 1,
    }
  );
  ApolloWrappers.writeQuery(
    {query: HAS_IN_PROGRESS_ALPHAFOLD_PREDICTION},
    {
      protein: {__typename: 'ProteinNode', id: proteinId, alphafoldPredictionInProgress: true},
    },
    {proteinId}
  );
}

interface UnavailableStructureInfo {
  structureFile: null;
  versionId: null;
}

interface AvailableStructureInfo {
  structureFile: ProteinStructureExternalFile;
  structureFileOutOfDate: boolean;
  versionId: number;
}

/**
 * Utility to evaluate info about a Protein's AlphaFold structure files.
 *
 * structureFile: the most recent completed structure file available, or undefined if there are none.
 * structureFileOutOfDate: indicates whether structureFile belongs to the most recent protein version.
 */
export function getStructureInfoFromVersions(
  versions?: Array<ProteinVersion>
): UnavailableStructureInfo | AvailableStructureInfo {
  if (versions === undefined || versions.length === 0) {
    return {structureFile: null, versionId: null};
  }
  const currentVersionStructures = versions[versions.length - 1].structures;
  if (currentVersionStructures.length) {
    const versionId = versions[versions.length - 1].id;
    return {
      structureFile: currentVersionStructures[0].externalFile,
      structureFileOutOfDate: false,
      versionId,
    };
  }

  const flattenedVersionStructures = versions.flatMap((version) =>
    version.structures.map((structure) => ({structure, versionId: version.id}))
  );
  if (flattenedVersionStructures.length) {
    const {versionId, structure} = flattenedVersionStructures[flattenedVersionStructures.length - 1];
    return {
      structureFile: structure.externalFile,
      structureFileOutOfDate: true,
      versionId,
    };
  }
  return {structureFile: null, versionId: null};
}

export async function downloadStructureFile(
  structureFile: ProteinStructureExternalFile,
  proteinId: string,
  versionId: number
): Promise<void> {
  const signedURL = await performSignedFileQuery(structureFile.id, structureFile.url, proteinId, versionId);
  if (signedURL == null) {
    return;
  }
  window.open(signedURL, '_blank', 'noopener noreferrer');
  trackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_ALPHAFOLD_STRUCTURE);
}
